/**
 * Length-aware shrink-to-fit for a keycap binding name. The name font is nominally
 * `unit * 0.2`, but a long name then wraps to three-plus lines on a small cap and rides up over the
 * legend (measured: "Transcendence" / "Switch Weapon" hit three lines on a 34px Destiny cap). This
 * shrinks the font just enough to keep the name within two lines, down to a legibility floor, so
 * long names stay readable on dense boards. Short names are unaffected.
 *
 * Pure and deterministic: a rough average glyph advance (measured against the keycap name font)
 * estimates how many characters fit per line at a given size; the result is the largest font that
 * fits the name in two lines, clamped to [floor, base].
 */

// average glyph advance as a fraction of font size, for the keycap name font (weight 600). Canvas
// measurement on the live render put the word ratio at ~0.55; the 0.62 here adds a margin for the
// browser's greedy line-fill, which wastes part of each line and otherwise spills a char to a 3rd line.
const GLYPH_W = 0.62;
const MAX_LINES = 2;

/** The shrink-to-fit font size (px) for a keycap's binding name. `capW` is the cap width in px. */
export function nameFontSize(name: string, capW: number, unit: number): number {
  const base = Math.max(8, unit * 0.2);
  const floor = Math.max(6, Math.round(unit * 0.14));
  const avail = Math.max(1, capW - 6); // the .kc 1px border + the .kc-name 2px side insets
  // the busiest of MAX_LINES lines must hold this many chars; size so that many fit on one line.
  // Using the per-line ceiling (not average density) is what keeps a long word inside two lines -
  // the greedy breaker fills line by line, so a fractional overflow becomes a whole extra line.
  const perLine = Math.ceil(Math.max(1, name.length) / MAX_LINES);
  const fit = avail / (GLYPH_W * perLine);
  return Math.round(Math.max(floor, Math.min(base, fit)));
}
